// src/admin/components/FontStyleControls.jsx
import React from "react";

// Font families options
export const FONT_FAMILIES = [
  { value: "inherit", label: "Default (Inherit)" },
  { value: "'Inter', sans-serif", label: "Inter" },
  { value: "'Roboto', sans-serif", label: "Roboto" },
  { value: "'Open Sans', sans-serif", label: "Open Sans" },
  { value: "'Poppins', sans-serif", label: "Poppins" },
  { value: "'Montserrat', sans-serif", label: "Montserrat" },
  { value: "'Playfair Display', serif", label: "Playfair Display (Serif)" },
  { value: "'Merriweather', serif", label: "Merriweather (Serif)" },
  { value: "'Source Sans Pro', sans-serif", label: "Source Sans Pro" },
  { value: "'Lato', sans-serif", label: "Lato" },
  { value: "'Nunito', sans-serif", label: "Nunito" },
];

// Font weights
export const FONT_WEIGHTS = [
  { value: "300", label: "Light (300)" },
  { value: "400", label: "Regular (400)" },
  { value: "500", label: "Medium (500)" },
  { value: "600", label: "Semi-bold (600)" },
  { value: "700", label: "Bold (700)" },
  { value: "800", label: "Extra-bold (800)" },
  { value: "900", label: "Black (900)" },
];

// Text alignment options
export const TEXT_ALIGNMENTS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

// Default font styles
export const DEFAULT_FONT_STYLES = {
  heading: {
    fontFamily: "inherit",
    fontSize: "2.5rem",
    fontWeight: "700",
    color: "#000000",
    lineHeight: "1.2",
  },
  subHeading: {
    fontFamily: "inherit",
    fontSize: "1.5rem",
    fontWeight: "600",
    color: "#333333",
    lineHeight: "1.4",
  },
  content: {
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: "400",
    color: "#555555",
    lineHeight: "1.6",
  },
  button: {
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: "500",
    color: "#ffffff",
  },
  stats: {
    fontFamily: "inherit",
    fontSize: "2rem",
    fontWeight: "700",
    color: "#000000",
    lineHeight: "1.2",
  },
  sectionTitle: {
    fontFamily: "inherit",
    fontSize: "2rem",
    fontWeight: "700",
    color: "#000000",
    lineHeight: "1.2",
  },
  sectionDescription: {
    fontFamily: "inherit",
    fontSize: "1.1rem",
    fontWeight: "400",
    color: "#555555",
    lineHeight: "1.6",
  },
  formTitle: {
    fontFamily: "inherit",
    fontSize: "1.8rem",
    fontWeight: "700",
    color: "#000000",
    lineHeight: "1.2",
  },
  formDescription: {
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: "400",
    color: "#333333",
    lineHeight: "1.5",
  },
  formInput: {
    fontFamily: "inherit",
    fontSize: "0.95rem",
    fontWeight: "400",
    color: "#333333",
    lineHeight: "1.5",
  },
  sideContentHeading: {
    fontFamily: "inherit",
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#000000",
    lineHeight: "1.4",
  },
  sideContentHighlight: {
    fontFamily: "inherit",
    fontSize: "2rem",
    fontWeight: "700",
    color: "#d4af37",
    lineHeight: "1.2",
  },
  sideContentBullet: {
    fontFamily: "inherit",
    fontSize: "1rem",
    fontWeight: "400",
    color: "#333333",
    lineHeight: "1.5",
  },
  sideContentFooter: {
    fontFamily: "inherit",
    fontSize: "0.875rem",
    fontWeight: "600",
    color: "#000000",
    lineHeight: "1.4",
  },
};

// Font Style Controls Component
const FontStyleControls = React.memo(function FontStyleControls({
  title,
  fontStyle = DEFAULT_FONT_STYLES.heading,
  onUpdate,
  showColor = true,
  showLineHeight = true,
}) {
  const updateStyle = (property, value) => {
    onUpdate({ ...fontStyle, [property]: value });
  };

  const colorValue = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(fontStyle.color || "")
    ? fontStyle.color
    : "#000000";

  return (
    <div className="hcfg-font-controls-group">
      <h4 style={{ marginBottom: "15px", fontSize: "16px" }}>{title}</h4>
      <div className="hcfg-font-controls">
        <div>
          <label>Font Family</label>
          <select
            value={fontStyle.fontFamily || "inherit"}
            onChange={(e) => updateStyle("fontFamily", e.target.value)}
          >
            {FONT_FAMILIES.map((font) => (
              <option key={font.value} value={font.value}>
                {font.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Font Size</label>
          <input
            type="text"
            value={fontStyle.fontSize || ""}
            onChange={(e) => updateStyle("fontSize", e.target.value)}
            placeholder="e.g., 2.5rem"
          />
        </div>
        <div>
          <label>Font Weight</label>
          <select
            value={fontStyle.fontWeight || "400"}
            onChange={(e) => updateStyle("fontWeight", e.target.value)}
          >
            {FONT_WEIGHTS.map((weight) => (
              <option key={weight.value} value={weight.value}>
                {weight.label}
              </option>
            ))}
          </select>
        </div>
        {showColor && (
          <>
            <div>
              <label>Color</label>
              <input
                type="color"
                value={colorValue}
                onChange={(e) => updateStyle("color", e.target.value)}
                style={{ width: "100%", height: "40px" }}
              />
            </div>
            <div>
              <label>Color Code</label>
              <input
                type="text"
                value={fontStyle.color || ""}
                onChange={(e) => updateStyle("color", e.target.value)}
                placeholder="#000000"
              />
            </div>
          </>
        )}
        {showLineHeight && (
          <div>
            <label>Line Height</label>
            <input
              type="text"
              value={fontStyle.lineHeight || "1.5"}
              onChange={(e) => updateStyle("lineHeight", e.target.value)}
              placeholder="e.g., 1.5"
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default FontStyleControls;
